import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import ModalFeedback from '../components/ModalFeedback'
import { getApiBaseUrl } from '../utils/apiConfig'
import axios from 'axios'
import useRolePermissions from '../hooks/useRolePermissions'

const API_BASE_URL = getApiBaseUrl()

export default function AccountTransactions() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const { canMutate, isViewer } = useRolePermissions()
  const [transactions, setTransactions] = useState([])
  const [chartOfAccounts, setChartOfAccounts] = useState([])
  const [realBusinessTransactions, setRealBusinessTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRealData, setShowRealData] = useState(true) // Toggle between manual and real transactions
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('create') // create, view
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [dateFilter, setDateFilter] = useState('')

  // New transaction form state
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    transaction_type: 'receipt',
    amount: '',
    cash_account_id: '',
    contra_account_id: '',
    description: '',
    reference: ''
  })

  useEffect(() => {
    fetchAccountTransactions()
    fetchChartOfAccounts()
    fetchRealBusinessTransactions()
  }, [])

  const fetchAccountTransactions = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const params = new URLSearchParams()
      if (typeFilter) params.append('transaction_type', typeFilter)
      if (dateFilter) params.append('date', dateFilter)

      const response = await axios.get(`${API_BASE_URL}/banking/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setTransactions(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch account transactions:', error)
      setError(error.response?.data?.message || error.message || 'Failed to fetch account transactions')
    } finally {
      setLoading(false)
    }
  }

  const fetchChartOfAccounts = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_BASE_URL}/chart-of-accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setChartOfAccounts(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch chart of accounts:', error)
    }
  }

  const fetchRealBusinessTransactions = async () => {
    try {
      const token = localStorage.getItem('token')

      // Fetch actual invoices, expenses, and salaries
      const [invoicesResponse, expensesResponse, salariesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/expenses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/salaries`, { headers: { Authorization: `Bearer ${token}` } })
      ])

      const realTransactions = []
      let globalIndex = 0 // Ensure global uniqueness

      // Process invoices as money coming in
      if (invoicesResponse.data.data) {
        invoicesResponse.data.data.forEach((invoice, index) => {
          const invoiceId = invoice.invoice_id || invoice.id || `inv-${globalIndex++}`
          const uniqueKey = `inv-${invoiceId}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`
          realTransactions.push({
            transaction_date: invoice.invoice_date || new Date().toISOString().split('T')[0],
            transaction_type: 'receipt',
            amount: invoice.invoice_amount || 0,
            description: `Invoice payment - ${invoice.project_name || 'Project'}`,
            reference: `INV-${invoice.invoice_number || invoiceId}`,
            source: 'invoices',
            source_id: invoiceId,
            unique_key: uniqueKey, // Add unique key for React
            client_name: invoice.client_name || 'Unknown'
          })
        })
      }

      // Process expenses as money going out
      if (expensesResponse.data.data) {
        expensesResponse.data.data.forEach((expense, index) => {
          const expenseId = expense.expense_id || expense.id || `exp-${globalIndex++}`
          const uniqueKey = `exp-${expenseId}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`
          realTransactions.push({
            transaction_date: expense.expense_date || new Date().toISOString().split('T')[0],
            transaction_type: 'payment',
            amount: expense.amount || 0,
            description: `Expense - ${expense.category_name || expense.description || 'General expense'}`,
            reference: `EXP-${expenseId}`,
            source: 'expenses',
            source_id: expenseId,
            unique_key: uniqueKey, // Add unique key for React
            vendor_name: expense.vendor_name || 'Various'
          })
        })
      }

      // Process salaries as money going out
      if (salariesResponse.data.data) {
        salariesResponse.data.data.forEach((salary, index) => {
          const salaryId = salary.salary_id || salary.id || `sal-${globalIndex++}`
          const uniqueKey = `sal-${salaryId}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`
          realTransactions.push({
            transaction_date: salary.payment_date || new Date().toISOString().split('T')[0],
            transaction_type: 'payment',
            amount: salary.net_salary || 0,
            description: `Salary payment - ${salary.employee_name || 'Employee'}`,
            reference: `SAL-${salaryId}`,
            source: 'salaries',
            source_id: salaryId,
            unique_key: uniqueKey, // Add unique key for React
            employee_name: salary.employee_name || 'Unknown'
          })
        })
      }

      // Sort by date descending
      realTransactions.sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date))

      setRealBusinessTransactions(realTransactions)
    } catch (error) {
      console.error('Failed to fetch real business transactions:', error)
      // Fallback to financial data if individual fetches fail
      fetchFinancialDataFallback()
    }
  }

  const fetchFinancialDataFallback = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_BASE_URL}/reports/financial-data`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data.success) {
        const financialData = response.data.data
        const realTransactions = []

        // Fallback summary transactions
        if (financialData.summary) {
          if (financialData.summary.totalRevenue > 0) {
            realTransactions.push({
              transaction_date: new Date().toISOString().split('T')[0],
              transaction_type: 'receipt',
              amount: financialData.summary.totalRevenue,
              description: 'Total revenue from all sources',
              reference: 'TOTAL-REV',
              source: 'summary'
            })
          }
        }

        setRealBusinessTransactions(realTransactions)
      }
    } catch (error) {
      console.error('Failed to fetch financial data:', error)
    }
  }

  const handleCreate = () => {
    setModalType('create')
    setFormData({
      transaction_date: new Date().toISOString().split('T')[0],
      transaction_type: 'receipt',
      amount: '',
      cash_account_id: '',
      contra_account_id: '',
      description: '',
      reference: `${Date.now().toString().slice(-6)}`
    })
    setShowModal(true)
  }

  const handleView = (transaction) => {
    setModalType('view')
    setSelectedTransaction(transaction)
    setShowModal(true)
  }

  const handleDelete = async (transactionId) => {
    if (!canMutate) {
      showError('You do not have permission to delete account transactions')
      return
    }
    if (!confirm('Are you sure you want to delete this account transaction? This will also delete the associated journal entry.')) return

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_BASE_URL}/banking/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      showSuccess('Account transaction deleted successfully')
      await fetchAccountTransactions()
    } catch (error) {
      console.error('Failed to delete account transaction:', error)
      showError(error.response?.data?.message || 'Failed to delete account transaction')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const token = localStorage.getItem('token')
      const transactionData = {
        transaction_date: formData.transaction_date,
        transaction_type: formData.transaction_type,
        amount: parseFloat(formData.amount),
        cash_account_id: parseInt(formData.cash_account_id),
        contra_account_id: parseInt(formData.contra_account_id),
        description: formData.description,
        reference: formData.reference
      }

      await axios.post(`${API_BASE_URL}/banking/transactions`, transactionData, {
        headers: { Authorization: `Bearer ${token}` }
      })

      showSuccess('Account transaction recorded successfully')
      setShowModal(false)
      await fetchAccountTransactions()
    } catch (error) {
      console.error('Failed to save account transaction:', error)
      showError(error.response?.data?.message || 'Failed to save account transaction')
    }
  }

  const showSuccess = (message) => {
    alert(message)
  }

  const showError = (message) => {
    alert(message)
  }

  // Use real business transactions if toggle is on, otherwise use manual transactions
  const displayTransactions = showRealData ? realBusinessTransactions : transactions

  const filteredTransactions = displayTransactions.filter(transaction => {
    const matchesSearch = searchTerm === '' ||
      (transaction.description && transaction.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (transaction.reference && transaction.reference.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesType = !typeFilter || transaction.transaction_type === typeFilter
    const matchesDate = !dateFilter || transaction.transaction_date === dateFilter
    return matchesSearch && matchesType && matchesDate
  })

  const getBankAccounts = () => {
    return chartOfAccounts.filter(account =>
      account.account_name.toLowerCase().includes('bank') ||
      account.account_name.toLowerCase().includes('cash') ||
      account.account_code.startsWith('1')
    )
  }

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'receipt': return '📥'
      case 'payment': return '📤'
      case 'transfer': return '🔄'
      default: return '💳'
    }
  }

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'receipt': return 'Bank Receipt'
      case 'payment': return 'Bank Payment'
      case 'transfer': return 'Bank Transfer'
      default: return 'Transaction'
    }
  }

  if (loading) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⏳</div>
        <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>Loading account transactions...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`rounded-lg p-8 text-center ${isDark ? 'bg-slate-900 text-white' : 'bg-white'}`}>
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchAccountTransactions}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          🔄 Retry
        </button>
      </div>
    )
  }

  const bgColor = isDark ? 'bg-slate-900' : 'bg-white'
  const textColor = isDark ? 'text-white' : 'text-gray-800'
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200'
  const cardBgColor = isDark ? 'bg-slate-800' : 'bg-gray-50'
  const inputBgColor = isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-700'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${textColor}`}>💰 Account Transactions</h2>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Manage bank receipts, payments, and transfers</p>
        </div>
        {canMutate && (
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
          >
            ＋ New Transaction
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📥</span>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Total Receipts</span>
          </div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(displayTransactions.filter(t => t.transaction_type === 'receipt').reduce((sum, t) => sum + (t.amount || 0), 0))}
          </div>
        </div>

        <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📤</span>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Total Payments</span>
          </div>
          <div className="text-2xl font-bold text-red-600">
            {formatCurrency(displayTransactions.filter(t => t.transaction_type === 'payment').reduce((sum, t) => sum + (t.amount || 0), 0))}
          </div>
        </div>

        <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">💰</span>
            <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Net Cash Flow</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {formatCurrency(
              displayTransactions.filter(t => t.transaction_type === 'receipt').reduce((sum, t) => sum + (t.amount || 0), 0) -
              displayTransactions.filter(t => t.transaction_type === 'payment').reduce((sum, t) => sum + (t.amount || 0), 0)
            )}
          </div>
        </div>
      </div>

      {/* Data Source Toggle */}
      <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`font-semibold ${textColor}`}>Transaction Source</h3>
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
              {showRealData ? '📊 Showing real business transactions from invoices, expenses, and salaries' : '💳 Showing manual bank transactions'}
            </p>
          </div>
          <button
            onClick={() => setShowRealData(!showRealData)}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              showRealData
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {showRealData ? '📊 Real Data' : '💳 Manual Data'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="🔍 Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
            />
          </div>
          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className={`rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
            >
              <option value="">All Types</option>
              <option value="receipt">Receipts</option>
              <option value="payment">Payments</option>
              <option value="transfer">Transfers</option>
            </select>
          </div>
          <div>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={`rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
            />
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className={`${bgColor} rounded-lg overflow-hidden border ${borderColor}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-slate-700' : 'bg-gray-100'}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction, index) => (
                  <tr key={
                    transaction.unique_key || // First priority: unique_key for real transactions
                    transaction.cash_transaction_id || // Second priority: manual transaction ID
                    `${transaction.source || 'trans'}-${index}-${Date.now()}` // Final fallback: guaranteed unique
                  } className={isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 text-sm">{formatDate(transaction.transaction_date)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="flex items-center gap-1">
                        <span>{getTransactionIcon(transaction.transaction_type)}</span>
                        <span>{getTransactionLabel(transaction.transaction_type)}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{transaction.reference || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-col">
                        <span>{transaction.description || 'No description'}</span>
                        {showRealData && transaction.source && (
                          <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            {transaction.source === 'invoices' && `👤 Client: ${transaction.client_name || 'Various'}`}
                            {transaction.source === 'expenses' && `🏪 Vendor: ${transaction.vendor_name || 'Various'}`}
                            {transaction.source === 'salaries' && `👷 Employee: ${transaction.employee_name || 'Various'}`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-medium ${
                      transaction.transaction_type === 'receipt' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.transaction_type === 'receipt' ? '+' : '-'}{formatCurrency(transaction.amount || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleView(transaction)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                          title="View Details"
                        >
                          👁️
                        </button>
                        {canMutate && (
                          <button
                            onClick={() => handleDelete(transaction.cash_transaction_id)}
                            className="text-red-600 hover:text-red-800 font-medium"
                            title="Delete Transaction"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <div className="text-4xl mb-2">{showRealData ? '💼' : '💳'}</div>
                    <p className={isDark ? 'text-slate-400' : 'text-gray-600'}>
                      {showRealData
                        ? 'No business transactions found. Create invoices, expenses, or salaries to see money flow here.'
                        : 'No account transactions found'}
                    </p>
                    {showRealData ? (
                      <div className="mt-4 space-y-2">
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                          💡 Tip: Switch to manual mode to record bank transactions directly
                        </p>
                        <button
                          onClick={() => setShowRealData(false)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                        >
                          Switch to Manual Transactions
                        </button>
                      </div>
                    ) : canMutate && (
                      <button
                        onClick={handleCreate}
                        className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
                      >
                        Record First Transaction
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Create/View - Using the same structure as CashTransactions */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${bgColor} rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
            <div className="sticky top-0 bg-inherit p-6 border-b flex justify-between items-center z-10">
              <h2 className={`text-xl font-bold ${textColor}`}>
                {modalType === 'view' ? '👁️ View Account Transaction' : '💳 New Account Transaction'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {modalType === 'view' ? (
                /* View Mode */
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Transaction Date</label>
                      <p className={`text-lg ${textColor}`}>{formatDate(selectedTransaction.transaction_date)}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Type</label>
                      <p className={`text-lg ${textColor}`}>
                        {getTransactionIcon(selectedTransaction.transaction_type)} {getTransactionLabel(selectedTransaction.transaction_type)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Reference</label>
                      <p className={`text-lg ${textColor}`}>{selectedTransaction.reference || 'N/A'}</p>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Amount</label>
                      <p className={`text-lg font-bold ${selectedTransaction.transaction_type === 'receipt' ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedTransaction.transaction_type === 'receipt' ? '+' : '-'}{formatCurrency(selectedTransaction.amount || 0)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
                    <p className={`text-lg ${textColor}`}>{selectedTransaction.description || 'No description'}</p>
                  </div>

                  {selectedTransaction.journal_entry_id && (
                    <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-gray-50'}`}>
                      <p className={`text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>✓ Journal Entry Created</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Entry ID: {selectedTransaction.journal_entry_id}</p>
                    </div>
                  )}
                </div>
              ) : (
                /* Create Mode - Form implementation similar to CashTransactions */
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Transaction Date *</label>
                      <input
                        type="date"
                        value={formData.transaction_date}
                        onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                        required
                        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Transaction Type *</label>
                      <select
                        value={formData.transaction_type}
                        onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                        required
                        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
                      >
                        <option value="receipt">📥 Receipt (Money In)</option>
                        <option value="payment">📤 Payment (Money Out)</option>
                        <option value="transfer">🔄 Transfer (Between Accounts)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Amount *</label>
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Bank Account *</label>
                      <select
                        value={formData.cash_account_id}
                        onChange={(e) => setFormData({ ...formData, cash_account_id: e.target.value })}
                        required
                        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
                      >
                        <option value="">Select Bank Account</option>
                        {getBankAccounts().map(account => (
                          <option key={account.account_id} value={account.account_id}>
                            {account.account_code} - {account.account_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Contra Account *</label>
                      <select
                        value={formData.contra_account_id}
                        onChange={(e) => setFormData({ ...formData, contra_account_id: e.target.value })}
                        required
                        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
                      >
                        <option value="">Select Contra Account</option>
                        {chartOfAccounts.map(account => (
                          <option key={account.account_id} value={account.account_id}>
                            {account.account_code} - {account.account_name} ({account.account_type})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Reference</label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      placeholder="Transaction reference (optional)"
                      className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      placeholder="Transaction description..."
                      className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBgColor}`}
                    />
                  </div>

                  <div className="flex gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border rounded-lg font-medium transition hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!canMutate}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50"
                    >
                      Record Transaction
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
